/* eslint-disable no-spaced-func */
/** @jsx jsx */

import { ComponentProps, Fragment, ReactNode, useState } from 'react';
import {
  IconButton,
  LegacyButton,
  Modal,
  ModalContent,
  ModalContentBody,
  ModalContentFooter,
  ModalContentHeader,
  Spacer,
} from '@zapier/design-system';
import { MenuTrigger } from '@zapier/design-system-beta';
import { css, jsx } from '@emotion/core';

const styleRoot = css`
  position: relative;
  white-space: nowrap;
  display: flex;
  justify-content: flex-end;
`;

const styleModalWrapper = css`
  text-align: initial;
`;

type Item = {
  /** Will trigger a confirmation when the option is chosen */
  isConfirmationRequired?: boolean;
  /** Renders a disabled option */
  isDisabled?: boolean;
  /** Indicates if this item is a dangerous action (e.g., Delete) */
  isDanger?: boolean;
  /** Content to be rendered in the menu item */
  content: ReactNode;
  /** Optional text to be displayed in the confirmation modal body */
  modalTextContent?: string;
  /** Optional text to be displayed in the confirmation modal title */
  modalTitle?: string;
  /** Callback that handles the clicked option */
  onChoose?: () => void;
  /** Key value of each option*/
  value: string;
};

type Props = {
  /** Modal confirmation button text */
  confirmationText?: string;
  /** Icon that triggers the dropdown. The default is `navCogFill` */
  iconName?: ComponentProps<typeof IconButton>['name'];
  /** Array of actions to be rendered */
  items: Item[];
};

/** Renders a menu toggle with a gear icon. Clicking opens a menu of items. */
export default function ActionsDropdownMenu({
  confirmationText = 'Confirm',
  iconName = 'navMoreHoriz',
  items,
}: Props) {
  const [itemPendingConfirmation, setItemPendingConfirmation] = useState<
    Item | undefined
  >();

  function handleItemAction(item: Item) {
    if (item.isDisabled) {
      return;
    }
    if (item.isConfirmationRequired) {
      setItemPendingConfirmation(item);
      return;
    }
    if (item.onChoose) {
      item.onChoose();
    }
  }

  function modalCancel() {
    setItemPendingConfirmation(undefined);
  }

  function modalConfirm() {
    if (itemPendingConfirmation?.onChoose) {
      itemPendingConfirmation.onChoose();
    }
    setItemPendingConfirmation(undefined);
  }

  return (
    <Fragment>
      {itemPendingConfirmation && (
        <div css={styleModalWrapper}>
          <Modal onClosed={modalCancel}>
            <ModalContent>
              <ModalContentHeader>
                {itemPendingConfirmation.modalTitle}
              </ModalContentHeader>
              <ModalContentBody>
                {itemPendingConfirmation.modalTextContent}
              </ModalContentBody>
              <Spacer height="10px" />
              <ModalContentFooter>
                <LegacyButton color="secondary" onClick={modalCancel}>
                  Cancel
                </LegacyButton>
                <LegacyButton color="primary" onClick={modalConfirm}>
                  {confirmationText}
                </LegacyButton>
              </ModalContentFooter>
            </ModalContent>
          </Modal>
        </div>
      )}
      <div css={styleRoot}>
        <MenuTrigger.Root>
          <IconButton
            aria-label="Options toggler"
            name={iconName}
            variant="ghost"
          />
          <MenuTrigger.Popover>
            <MenuTrigger.List aria-label="Options menu">
              {items.map(item => (
                <MenuTrigger.Item
                  isDisabled={item.isDisabled}
                  key={item.value}
                  onAction={() => handleItemAction(item)}
                  variant={item.isDanger ? 'danger' : 'primary'}
                >
                  {item.content}
                </MenuTrigger.Item>
              ))}
            </MenuTrigger.List>
          </MenuTrigger.Popover>
        </MenuTrigger.Root>
      </div>
    </Fragment>
  );
}
